/**
 * MindIngestStats — vibe-designer 1.174.0 Phase 5β2.
 *
 * Per-service viz on the MIND detail panel. Calls
 * GET /api/knowledge/services/mind/stats-extended → renders
 *   - chunks-per-day timeline (recharts LineChart, lazy-loaded)
 *   - per-table breakdown (4 rows: mind_docs/configs/code/sessions)
 *
 * recharts is loaded via lazy()/Suspense so the ~95 KB gzipped chunk
 * only fires when the user navigates to MIND detail (bundle audit
 * recorded 2026-05-20 — see worldview §5).
 *
 * Empty-state cases (LIVE-VERIFIED 2026-05-20 from dallas):
 *   - available=false + error → honest-error banner
 *     (mind_vectors connection params missing on this tenant)
 *   - available=true + empty chunks_per_day + empty per_table → empty state
 *   - available=true + non-zero breakdown but zero chunks_per_day
 *     (e.g., dallas: mind_sessions=32 but all 6 weeks old) → still
 *     renders breakdown + empty-chart helper text
 */
import { useEffect, Suspense, lazy } from 'react';
import { Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import type { MindChunksPerDayPoint, MindPerTableBreakdown } from '@/services/knowledgeApi';

// Lazy-load recharts so the ~95 KB gzipped chunk only fires when the
// MIND detail panel mounts. Splits CHART into its own React.lazy unit
// so the per-table breakdown can render instantly while the chart
// dependency loads in the background.
const ChunksPerDayChart = lazy(() => import('./_MindChunksPerDayChart'));

export function MindIngestStats() {
  const data = useKnowledgeStore((s) => s.mindStatsExtended);
  const isFetching = useKnowledgeStore((s) => s.isFetchingMindStatsExtended);
  const error = useKnowledgeStore((s) => s.mindStatsExtendedError);
  const fetchMindStatsExtended = useKnowledgeStore((s) => s.fetchMindStatsExtended);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);

  useEffect(() => {
    void fetchMindStatsExtended();
  }, [fetchMindStatsExtended, lastIngestAt]);

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary flex items-center gap-1.5">
          <BarChart3 className="w-3 h-3" />
          MIND ingest stats
        </h2>
        <button
          data-testid="mind-stats-extended-refresh"
          type="button"
          onClick={() => void fetchMindStatsExtended()}
          disabled={isFetching}
          className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
        >
          {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          <span>Refresh</span>
        </button>
      </header>

      {error && (
        <div
          data-testid="mind-stats-extended-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && data && data.error && (
        <div
          data-testid="mind-stats-extended-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {data.error}
        </div>
      )}

      {!error && data && !data.error && (
        <>
          {/* Phase 6.4-B BUG-MIND-CHART-1 LOCK — the breakdown shows ALL-TIME
              vectors; the chart below shows last-30-days ingest activity. The
              two are semantically different (e.g. mind_sessions=32 ALL-TIME
              vs chart Y-axis maxing at 4 in last 30 days). Pre-fix lacked
              section labels so users misread the legend as "chart should
              cover mind_sessions=32 too". Inline labels disambiguate. */}
          <p className="text-xs font-medium text-vibe-text-secondary uppercase tracking-wide mb-1">
            All-time vectors by table
          </p>
          <BreakdownRows breakdown={data.per_table_breakdown} />

          {data.chunks_per_day_30d.length === 0 ? (
            <div
              data-testid="mind-stats-extended-empty"
              className="text-xs text-vibe-text-secondary py-2"
            >
              No chunks ingested in the last 30 days.
            </div>
          ) : (
            <>
              <p className="text-xs font-medium text-vibe-text-secondary uppercase tracking-wide mt-2 mb-0.5">
                Last 30 days ingest activity (all tables combined)
              </p>
              <div className="h-40 w-full">
                <Suspense fallback={<div className="text-xs text-vibe-text-secondary">Loading chart…</div>}>
                  <ChunksPerDayChart points={data.chunks_per_day_30d} />
                </Suspense>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function BreakdownRows({ breakdown }: { breakdown: MindPerTableBreakdown }) {
  const rows: Array<[keyof MindPerTableBreakdown, string]> = [
    ['mind_docs', 'docs'],
    ['mind_configs', 'configs'],
    ['mind_code', 'code'],
    ['mind_sessions', 'sessions'],
  ];
  return (
    <div data-testid="mind-stats-extended-breakdown" className="mb-2">
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {rows.map(([key, label]) => (
          <li
            key={key}
            data-testid={`mind-stats-breakdown-row-${key}`}
            className="flex items-center justify-between px-2 py-0.5 text-xs rounded hover:bg-vibe-bg"
          >
            <span className="font-mono text-vibe-text-secondary truncate">
              {key} <span className="text-vibe-text-secondary/70">— {label}</span>
            </span>
            <span className="tabular-nums text-vibe-text">{breakdown[key]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export type { MindChunksPerDayPoint };
